import { Copy, Globe } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/shared";
import { flowService } from "@/lib/services";
import type { FlowTrigger } from "@/types";

interface FlowWebhookSectionProps {
  flowId: string;
  triggers: FlowTrigger[];
  onTriggersChange: (triggers: FlowTrigger[]) => void;
}

export function FlowWebhookSection({ flowId, triggers, onTriggersChange }: Readonly<FlowWebhookSectionProps>) {
  const { showToast } = useToast();
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);
  const [creatingWebhook, setCreatingWebhook] = useState(false);

  const webhookTrigger = triggers.find((t) => t.triggerType === "webhook" && t.webhook);

  const handleCopyUrl = () => {
    const url =
      webhookTrigger?.webhook?.webhookUrl || `${window.location.origin}/api/v1/hooks/${webhookTrigger?.webhook?.slug}`;
    navigator.clipboard.writeText(url);
    showToast("URL copied", "success");
  };

  const handleRegenerateSecret = async () => {
    const confirmed = window.confirm(
      "Are you sure? The current secret will stop working immediately. Any external systems using it will need to be updated.",
    );
    if (!confirmed || !webhookTrigger?.webhook) return;
    try {
      const res = await flowService.regenerateWebhookSecret(webhookTrigger.webhook.uuid);
      setWebhookSecret(res.data.secret);
      showToast("Secret regenerated", "success");
    } catch (err) {
      console.error("Error regenerating secret:", err);
      showToast("Failed to regenerate secret", "error");
    }
  };

  const handleEnableWebhook = async () => {
    setCreatingWebhook(true);
    try {
      const res = await flowService.createTrigger(flowId, {
        trigger_type: "webhook",
        name: "Webhook Trigger",
      });
      const data = res.data as FlowTrigger & { webhookSecret?: string };
      if (data.webhookSecret) {
        setWebhookSecret(data.webhookSecret);
      }
      const triggersRes = await flowService.getTriggers(flowId);
      onTriggersChange(triggersRes.data);
      showToast("Webhook enabled", "success");
    } catch (err) {
      console.error("Error creating webhook:", err);
      showToast("Failed to create webhook", "error");
    } finally {
      setCreatingWebhook(false);
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <h2 className="text-lg font-medium text-content-primary mb-4 flex items-center gap-2">
        <Globe className="h-5 w-5 text-brand-primary" />
        Webhook
      </h2>
      {webhookTrigger?.webhook ? (
        <div className="space-y-3">
          <div>
            <span className="block text-xs font-medium text-content-tertiary mb-1">Endpoint URL</span>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary font-mono break-all">
                {webhookTrigger.webhook.webhookUrl ||
                  `${window.location.origin}/api/v1/hooks/${webhookTrigger.webhook.slug}`}
              </code>
              <button
                type="button"
                onClick={handleCopyUrl}
                className="p-2 text-content-tertiary hover:text-content-primary transition-colors"
                title="Copy URL"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div>
            <span className="block text-xs font-medium text-content-tertiary mb-1">Secret</span>
            {webhookSecret ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-green-400 font-mono break-all">
                    {webhookSecret}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(webhookSecret);
                      showToast("Secret copied", "success");
                    }}
                    className="p-2 text-content-tertiary hover:text-content-primary transition-colors"
                    title="Copy secret"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-yellow-400">Save this secret now. It will not be shown again.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <code className="block px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-quaternary font-mono">
                  X-Webhook-Secret: ●●●●●●●●
                </code>
                <button
                  type="button"
                  onClick={handleRegenerateSecret}
                  className="text-xs text-content-tertiary hover:text-red-400 transition-colors"
                >
                  Regenerate secret
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 text-xs text-content-tertiary pt-1">
            <span>Status: {webhookTrigger.webhook.isActive ? "Active" : "Inactive"}</span>
            <span>Received: {webhookTrigger.webhook.receiveCount ?? 0} requests</span>
            {webhookTrigger.webhook.lastReceivedAt && (
              <span>Last: {new Date(webhookTrigger.webhook.lastReceivedAt).toLocaleString()}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <Globe className="h-10 w-10 text-content-quaternary mx-auto mb-3" />
          <p className="text-sm text-content-tertiary mb-4">
            Enable a webhook to trigger this workflow from external systems.
          </p>
          <button
            type="button"
            onClick={handleEnableWebhook}
            disabled={creatingWebhook}
            className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {creatingWebhook ? "Enabling..." : "Enable Webhook"}
          </button>
        </div>
      )}
    </div>
  );
}
